import type {
    SketchRegionPlane,
    SketchRegionVec2,
    SketchRegionVec3,
} from './SketchRegionTypes';

/** 坐标量化精度。 */
const COORDINATE_SCALE = 1_000_000;

/**
 * 从三维点减去三维点。
 * @param left 左值。
 * @param right 右值。
 * @returns 差向量。
 */
export function subtractVec3(left: SketchRegionVec3, right: SketchRegionVec3): SketchRegionVec3 {
    return [
        left[0] - right[0],
        left[1] - right[1],
        left[2] - right[2],
    ];
}

/**
 * 三维点线性插值。
 * @param start 起点。
 * @param end 终点。
 * @param t 插值参数。
 * @returns 插值点。
 */
export function lerpVec3(
    start: SketchRegionVec3,
    end: SketchRegionVec3,
    t: number,
): SketchRegionVec3 {
    return [
        start[0] + (end[0] - start[0]) * t,
        start[1] + (end[1] - start[1]) * t,
        start[2] + (end[2] - start[2]) * t,
    ];
}

/**
 * 二维点线性插值。
 * @param start 起点。
 * @param end 终点。
 * @param t 插值参数。
 * @returns 插值点。
 */
export function lerpVec2(
    start: SketchRegionVec2,
    end: SketchRegionVec2,
    t: number,
): SketchRegionVec2 {
    return [
        start[0] + (end[0] - start[0]) * t,
        start[1] + (end[1] - start[1]) * t,
    ];
}

/**
 * 三维点积。
 * @param left 左向量。
 * @param right 右向量。
 * @returns 点积。
 */
export function dotVec3(left: SketchRegionVec3, right: SketchRegionVec3): number {
    return left[0] * right[0] + left[1] * right[1] + left[2] * right[2];
}

/**
 * 二维叉积。
 * @param left 左向量。
 * @param right 右向量。
 * @returns 叉积标量。
 */
export function crossVec2(left: SketchRegionVec2, right: SketchRegionVec2): number {
    return left[0] * right[1] - left[1] * right[0];
}

/**
 * 二维减法。
 * @param left 左点。
 * @param right 右点。
 * @returns 差向量。
 */
export function subtractVec2(left: SketchRegionVec2, right: SketchRegionVec2): SketchRegionVec2 {
    return [left[0] - right[0], left[1] - right[1]];
}

/**
 * 将世界点投影到草图平面二维坐标。
 * @param point 世界点。
 * @param plane 草图平面。
 * @returns 平面坐标。
 */
export function projectPointToPlane(
    point: SketchRegionVec3,
    plane: SketchRegionPlane,
): SketchRegionVec2 {
    const relative = subtractVec3(point, plane.origin);
    return [
        dotVec3(relative, plane.xAxis),
        dotVec3(relative, plane.yAxis),
    ];
}

/**
 * 将草图平面二维坐标抬升到世界三维坐标。
 * @param point 平面坐标。
 * @param plane 草图平面。
 * @returns 世界点。
 */
export function liftPointFromPlane(
    point: SketchRegionVec2,
    plane: SketchRegionPlane,
): SketchRegionVec3 {
    return [
        plane.origin[0] + plane.xAxis[0] * point[0] + plane.yAxis[0] * point[1],
        plane.origin[1] + plane.xAxis[1] * point[0] + plane.yAxis[1] * point[1],
        plane.origin[2] + plane.xAxis[2] * point[0] + plane.yAxis[2] * point[1],
    ];
}

/**
 * 生成稳定顶点键。
 * @param point 二维点。
 * @returns 顶点键。
 */
export function vertexKey(point: SketchRegionVec2): string {
    return `${Math.round(point[0] * COORDINATE_SCALE)}:${Math.round(point[1] * COORDINATE_SCALE)}`;
}

/**
 * 判断两个二维点是否接近。
 * @param left 左点。
 * @param right 右点。
 * @param tolerance 容差。
 * @returns 是否接近。
 */
export function pointsAlmostEqual2(
    left: SketchRegionVec2,
    right: SketchRegionVec2,
    tolerance = 1e-7,
): boolean {
    return Math.hypot(left[0] - right[0], left[1] - right[1]) <= tolerance;
}

/**
 * 判断两个三维点是否接近。
 * @param left 左点。
 * @param right 右点。
 * @param tolerance 容差。
 * @returns 是否接近。
 */
export function pointsAlmostEqual3(
    left: SketchRegionVec3,
    right: SketchRegionVec3,
    tolerance = 1e-7,
): boolean {
    return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]) <= tolerance;
}

/**
 * 计算多边形有向面积。
 * @param points 多边形点。
 * @returns 有向面积。
 */
export function signedArea2(points: SketchRegionVec2[]): number {
    if (points.length < 3) {
        return 0;
    }
    let area = 0;
    for (let index = 0; index < points.length; index += 1) {
        const current = points[index]!;
        const next = points[(index + 1) % points.length]!;
        area += current[0] * next[1] - next[0] * current[1];
    }
    return area * 0.5;
}

/**
 * 计算多边形质心，退化时返回点平均值。
 * @param points 多边形点。
 * @returns 质心。
 */
export function polygonCentroid2(points: SketchRegionVec2[]): SketchRegionVec2 {
    const area = signedArea2(points);
    if (Math.abs(area) <= 1e-10) {
        const sum = points.reduce(
            (accumulator, point) => [accumulator[0] + point[0], accumulator[1] + point[1]] as SketchRegionVec2,
            [0, 0] as SketchRegionVec2,
        );
        return [
            sum[0] / Math.max(points.length, 1),
            sum[1] / Math.max(points.length, 1),
        ];
    }

    let cx = 0;
    let cy = 0;
    for (let index = 0; index < points.length; index += 1) {
        const current = points[index]!;
        const next = points[(index + 1) % points.length]!;
        const factor = current[0] * next[1] - next[0] * current[1];
        cx += (current[0] + next[0]) * factor;
        cy += (current[1] + next[1]) * factor;
    }
    return [cx / (6 * area), cy / (6 * area)];
}

/**
 * 点是否位于多边形内。
 * @param point 目标点。
 * @param polygon 多边形点。
 * @returns 是否位于内部。
 */
export function pointInPolygon2(point: SketchRegionVec2, polygon: SketchRegionVec2[]): boolean {
    let inside = false;
    for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index, index += 1) {
        const currentPoint = polygon[index]!;
        const previousPoint = polygon[previous]!;
        const intersects = ((currentPoint[1] > point[1]) !== (previousPoint[1] > point[1]))
            && (point[0] < (
                (previousPoint[0] - currentPoint[0])
                * (point[1] - currentPoint[1])
                / ((previousPoint[1] - currentPoint[1]) || Number.EPSILON)
                + currentPoint[0]
            ));
        if (intersects) {
            inside = !inside;
        }
    }
    return inside;
}

/**
 * 去掉重复闭合尾点。
 * @param points 原始点。
 * @returns 规范点列。
 */
export function normalizeLoopPoints3(points: SketchRegionVec3[]): SketchRegionVec3[] {
    if (points.length <= 1) {
        return [...points];
    }
    const result = [...points];
    while (result.length > 1 && pointsAlmostEqual3(result[0]!, result[result.length - 1]!)) {
        result.pop();
    }
    return result;
}

/**
 * 对字符串生成稳定短哈希。
 * @param value 原始字符串。
 * @returns 哈希字符串。
 */
export function hashString(value: string): string {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}
